import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { forkJoin } from 'rxjs';
import { IActivity } from '../../../Utils/_models/dashboard.model';
import { DashboardService } from '../../../Utils/_services/admin/dashboard/dashboard.service';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';


@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, ChartModule, SpinnerLoaderComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  showAllActivities = signal(false);
  statCards: any[] = [];
  recentActivities: IActivity[] = [];
  loader = signal(false)
  userGrowthData: any;
  userGrowthOptions: any;
  engagementData: any;
  engagementOptions: any;

  constructor(private dashboardService: DashboardService
  ) { }

  ngOnInit() {
    this.buildChartOptions();
    this.loader.set(true)
    forkJoin({
      stats: this.dashboardService.getStats(),
      activities: this.dashboardService.getAdminDashboardActvities()
    }).subscribe({
      next: ({ stats, activities }) => {
        this.buildStatCards(stats.data);
        this.buildCharts(stats.data);
        this.recentActivities = activities.data.activities;
        this.loader.set(false)
      }
    });
  }

  // ── Stat cards ──────────────────────────────────────

  private buildStatCards(data: any) {
    const s = data.stats;
    this.statCards = [
      {
        label: 'Total Users',
        value: s.totalUsers.value.toLocaleString(),
        change: s.totalUsers.growth,
        positive: !s.totalUsers.growth.startsWith('-'),
        gradient: true,
        icon: 'pi-users',
        iconBg: 'rgba(255,255,255,0.25)',
        iconColor: '#fff'
      },
      {
        label: 'Total Events',
        value: s.totalEvents.value.toLocaleString(),
        change: s.totalEvents.growth,
        positive: !s.totalEvents.growth.startsWith('-'),
        gradient: false,
        icon: 'pi-calendar',
        iconBg: '#fee2e2',
        iconColor: '#ef4444'
      },
      {
        label: 'Active Events',
        value: s.activeEvents.value.toLocaleString(),
        change: s.activeEvents.growth,
        positive: !s.activeEvents.growth.startsWith('-'),
        gradient: false,
        icon: 'pi-clock',
        iconBg: '#fef3c7',
        iconColor: '#f59e0b'
      },
      {
        label: 'Revenue',
        value: s.revenue.formatted,
        change: s.revenue.growth,
        positive: !s.revenue.growth.startsWith('-'),
        gradient: false,
        icon: 'pi-dollar',
        iconBg: '#fce7f3',
        iconColor: '#ec4899'
      }
    ];
  }

  // ── Charts ──────────────────────────────────────────

  private buildCharts(data: any) {
    const growthLabels = data.charts.userGrowth.map((d: any) => d.month);
    const growthValues = data.charts.userGrowth.map((d: any) => d.value);

    this.userGrowthData = {
      labels: growthLabels,
      datasets: [{
        data: growthValues,
        borderColor: '#6b7280',
        backgroundColor: 'rgba(107,114,128,0.07)',
        fill: true,
        tension: 0.4,
        pointRadius: growthValues.map((v: number) => v > 0 ? 5 : 0),
        pointHoverRadius: 6,
        pointBackgroundColor: '#1f2937',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        borderWidth: 2
      }]
    };

    const engLabels = data.charts.engagement.map((d: any) => d.month);
    const engValues = data.charts.engagement.map((d: any) => d.value);
    const maxVal = Math.max(...engValues);

    this.engagementData = {
      labels: engLabels,
      datasets: [{
        data: engValues,
        backgroundColor: engValues.map((v: number) =>
          v === maxVal && maxVal > 0 ? '#1f2937' : '#d1d5db'
        ),
        borderRadius: 6,
        barPercentage: 0.55,
        categoryPercentage: 0.7
      }]
    };
  }

  private buildChartOptions() {
    const textColor = '#9ca3af';
    const gridColor = '#f3f4f6';

    const scales = {
      x: {
        grid: { display: false },
        ticks: { color: textColor, font: { size: 11 } },
        border: { color: '#e5e7eb' }
      },
      y: {
        grid: { color: gridColor },
        ticks: {
          color: textColor,
          font: { size: 11 },
          callback: (v: any) => v >= 1000 ? (v / 1000) + 'k' : v
        },
        border: { display: false },
        min: 0
      }
    };

    const tooltip = {
      backgroundColor: '#1f2937',
      padding: 10,
      displayColors: false,
      callbacks: {
        title: () => '',
        label: (ctx: any) => ctx.parsed.y.toLocaleString()
      }
    };

    this.userGrowthOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip }, scales };
    this.engagementOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip }, scales };
  }

  // ── Activity helpers (used in template) ─────────────

  visibleActivities = computed(() =>
    this.showAllActivities()
      ? this.recentActivities
      : this.recentActivities.slice(0, 5)
  );

  toggleViewAll(): void {
    this.showAllActivities.update(v => !v);
  }


  getActivityIconBg(type: string): string {
    const map: Record<string, string> = {
      registration: '#ede9fe',
      payment: '#dcfce7',
      event: '#fee2e2'
    };
    return map[type] ?? '#f3f4f6';
  }

  getActivityIconColor(type: string): string {
    const map: Record<string, string> = {
      registration: '#7c3aed',
      payment: '#16a34a',
      event: '#ef4444'
    };
    return map[type] ?? '#6b7280';
  }

  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  getActivityDescription(item: IActivity): string {
    if (item.type === 'registration') return `${item.user.name} joined the platform`;
    if (item.type === 'payment') return `${item.payment?.formatted} payment from ${item.user.name}`;
    if (item.type === 'event') return `"${item.event?.title}" was created`;
    return item.user.name;
  }
}